import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import './MeetThePrimas.css';

function MeetThePrimas() {
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 800], [0, 200]);
  const heroScale = useTransform(scrollY, [0, 800], [1.05, 1.15]);
  const heroTextY = useTransform(scrollY, [0, 600], [0, -60]);
  const heroTextOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  const [introRef, introInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [philosophyRef, philosophyInView] = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <motion.div
      className="meet-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      {/* Full-bleed Hero with family photo */}
      <section className="meet-hero">
        <div className="meet-hero-image">
          <motion.img
            src="/images/homepage/meet-the-primas-family.png"
            alt="Katia and Patty with their families"
            style={{ y: heroY, scale: heroScale }}
          />
        </div>
        <div className="meet-hero-overlay" />
        <motion.div
          className="meet-hero-content"
          style={{ y: heroTextY, opacity: heroTextOpacity }}
        >
          <motion.span
            className="meet-hero-label"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            The Women Behind the Design
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.77, 0, 0.175, 1] }}
          >
            Meet the Primas
          </motion.h1>
          <motion.div
            className="meet-hero-line"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 1, delay: 0.8, ease: [0.77, 0, 0.175, 1] }}
          />
        </motion.div>
      </section>

      {/* Founders — photo + names integrated */}
      <section className="meet-founders" ref={introRef}>
        <div className="meet-founders-inner">
          <motion.div
            className="meet-founders-photo-col"
            initial={{ opacity: 0, x: -30 }}
            animate={introInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, ease: [0.4, 0, 0.2, 1] }}
          >
            <div className="meet-founders-photo">
              <img
                src="/images/homepage/meet-the-primas.png"
                alt="Katia Todd and Patty Sotela — the primas behind Casa Primas"
              />
              <div className="meet-founders-photo-border" />
            </div>
          </motion.div>
          <motion.div
            className="meet-founders-info"
            initial={{ opacity: 0, x: 30 }}
            animate={introInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
          >
            <span className="meet-founders-label">Co-Founders</span>
            <div className="meet-founders-name-block">
              <h2>Katia Todd</h2>
              <span className="meet-founders-amp">&</span>
              <h2>Patty Sotela</h2>
            </div>
            <div className="meet-founders-line" />
            <span className="meet-founders-tagline">Interior Design & Home Organization</span>
          </motion.div>
        </div>
      </section>

      {/* Design Philosophy — full-width editorial */}
      <section className="meet-philosophy" ref={philosophyRef}>
        <div className="meet-philosophy-inner">
          <motion.span
            className="section-label"
            initial={{ opacity: 0, y: 20 }}
            animate={philosophyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            Our Philosophy
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={philosophyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            Design Philosophy
          </motion.h2>
          <motion.div
            className="meet-philosophy-line"
            initial={{ scaleX: 0 }}
            animate={philosophyInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.77, 0, 0.175, 1] }}
          />
          <motion.p
            className="meet-philosophy-lead"
            initial={{ opacity: 0, y: 25 }}
            animate={philosophyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            Our design philosophy is rooted in the belief that beauty and function can <em>and should</em> coexist.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 25 }}
            animate={philosophyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            We begin by deeply understanding our clients' style preferences and how they live and interact within their homes, then thoughtfully design spaces that support those needs with ease and intention.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 25 }}
            animate={philosophyInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.7, delay: 0.5 }}
          >
            Every design is tailored to enhance daily life — seamlessly blending functionality, beauty, and personal style to create environments that feel elevated, intuitive, and uniquely theirs.
          </motion.p>
        </div>
      </section>

      {/* CTA */}
      <section className="meet-cta">
        <motion.div
          className="meet-cta-line"
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: [0.77, 0, 0.175, 1] }}
        />
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Ready to Work Together?
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          Let's create a space that's uniquely yours.
        </motion.p>
        <motion.a
          href="mailto:hello@casaprimas.com"
          className="cta-button"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          Get in Touch
        </motion.a>
      </section>
    </motion.div>
  );
}

export default MeetThePrimas;
